/**
 * sessionReturn.js — THE single decision for "the patient left and came back".
 *
 * ★★★ WHY THIS FILE EXISTS. The continue / PIN / re-login rule was spread across
 *     three owners that each held a piece of it: `usePrivacyScreen` decided
 *     continue-vs-lock inside a `useEffect`, `PatientSessionLockScreen` decided
 *     lock-vs-logout inside a countdown that only ran once the keypad was already
 *     mounted, and `useIdleTimeout` held a fourth clock next to both. Three owners
 *     for one question is how they disagreed, and they did:
 *
 *       - Coming back after 30+ minutes showed the PIN keypad FIRST, counted down,
 *         and only signed the patient out two seconds later.
 *       - Coming back within three seconds of ANY navigation continued straight to
 *         the page they left, however long they had been away, because the away
 *         clock was a component-local ref in a component the router destroys on
 *         every route change (`App.jsx` — `<Routes key={location.pathname}>`).
 *
 * ★★★ AND IT IS PURE, AND IT TAKES `now`. The decision it replaces lived inside a
 *     `useEffect` and read the clock internally, so no gate could reach it — the
 *     rule could only ever be described, never measured. `now` is REQUIRED and
 *     non-finite input throws, so a gate can prove the pin was honoured rather
 *     than assume it (D280).
 *
 * ★★★ IT FAILS CLOSED. An away stamp that is present but unreadable — corrupt,
 *     NaN, or in the future because the device clock moved — is NOT treated as
 *     "no time passed". Unknown elapsed time on a screen holding PHI resolves to
 *     a full re-login. Only a genuinely ABSENT stamp means "never went away".
 */

/** Below this, coming back is not an interruption — land where you left off. */
export const RESUME_GRACE_MS = 60 * 1000

/** Past this, a PIN is not enough. Overridable per user via `sessionPinWindow`. */
export const DEFAULT_PIN_WINDOW_MINUTES = 30

export const RETURN_CONTINUE = 'continue'
export const RETURN_LOCK = 'lock'
export const RETURN_LOGOUT = 'logout'

/** The PIN window in ms, defaulting anything absent or nonsensical to 30 minutes. */
export function pinWindowMs(pinWindowMinutes) {
  const m = Number(pinWindowMinutes)
  return (Number.isFinite(m) && m > 0 ? m : DEFAULT_PIN_WINDOW_MINUTES) * 60 * 1000
}

/**
 * What happens when the patient comes back.
 *
 * @param {number|null|undefined} awayAt  epoch ms the app was backgrounded, or null
 * @param {number}                now     epoch ms of the return — REQUIRED, never read internally
 * @param {boolean}               pinEnabled
 * @param {number}                [pinWindowMinutes=30]
 * @param {boolean}               [setupInProgress=false]  sign-up / setup not complete (PROD-1192)
 * @returns {'continue'|'lock'|'logout'}
 */
export function decideOnReturn({ awayAt, now, pinEnabled, pinWindowMinutes, setupInProgress } = {}) {
  if (!Number.isFinite(now)) {
    throw new TypeError('decideOnReturn: `now` must be a finite epoch — this function never reads the clock')
  }

  // Genuinely never went away. The ONLY input that continues without a time test.
  if (awayAt === null || awayAt === undefined) return RETURN_CONTINUE

  const stamp = Number(awayAt)
  if (!Number.isFinite(stamp)) return RETURN_LOGOUT

  const elapsed = now - stamp
  if (elapsed < 0) return RETURN_LOGOUT          // clock moved; elapsed is unknowable
  if (elapsed < RESUME_GRACE_MS) return RETURN_CONTINUE
  if (!pinEnabled) return RETURN_LOGOUT
  // PROD-1192 (Alex 2026-10-19): an app switch during sign-up and setup never asks for
  // the PIN. Only what would be the LOCK verdict is lifted; every LOGOUT stays as it was.
  if (setupInProgress === true && elapsed <= pinWindowMs(pinWindowMinutes)) return RETURN_CONTINUE
  return elapsed <= pinWindowMs(pinWindowMinutes) ? RETURN_LOCK : RETURN_LOGOUT
}

/**
 * PROD-1192 — is the patient still in sign-up / setup? The app's one setup-complete
 * signal is the profile's `onboardingCompleted` (server `onboarding_completed`), the same
 * flag ProtectedRoute uses to send a patient into /onboarding. Only an explicit `false`
 * is setup: a profile that could not be loaded (`undefined`) keeps the normal away rule.
 */
export function isSetupInProgress(profile) {
  return !!profile && profile.onboardingCompleted === false
}

/**
 * Has an EXISTING lock outlived its PIN window? Same rule, same constants, asked
 * from the lock screen while the keypad is up. Kept here so the boundary cannot
 * drift from the one `decideOnReturn` applies.
 */
export function isLockExpired({ lockedAt, now, pinWindowMinutes } = {}) {
  if (!Number.isFinite(now)) {
    throw new TypeError('isLockExpired: `now` must be a finite epoch')
  }
  if (lockedAt === null || lockedAt === undefined) return false
  const stamp = Number(lockedAt)
  if (!Number.isFinite(stamp)) return true
  const elapsed = now - stamp
  if (elapsed < 0) return true
  return elapsed > pinWindowMs(pinWindowMinutes)
}

// ═══ PROD-252 (P17 step 9) — THE FOREGROUND IDLE CLOCK ACROSS A RELOAD OR A REMOUNT ═══
//
// ★★ THE DEFECT. useIdleTimeout's effect set `lastActivityRef.current = Date.now()` every
//    time it ran and stored nothing. A reload, and every navigation (MobileLayout remounts:
//    `<Routes key={location.pathname}>`), handed the patient a fresh 30 minutes. A patient
//    idle for 20 minutes who reloaded was idled out at about 50 minutes, not 30.
//
// ★ THE RULE, and why it does not care whether a reload fires `visibilitychange: hidden`
//    (never measured on the web). The hook stores { lastActivityAt, hiddenAt }. A hide
//    shorter than RESUME_GRACE_MS (a reload, a quick app switch) carries the clock; a hide
//    of RESUME_GRACE_MS or more is a real background, which usePrivacyScreen already judged,
//    so the clock restarts (D269). With no hide recorded the clock simply carries.
//
// ★ IT FAILS CLOSED (P17 Q3, the md panel's PROD-108 polarity). A stamp that is present but
//    unreadable — NaN, garbage, negative, or further in the future than
//    IDLE_FUTURE_TOLERANCE_MS — is EXPIRED, never fresh: an unreadable stamp must not hand
//    anyone a new full timeout, because that is the defect itself. Only an ABSENT stamp is
//    fresh. The tolerance is the md panel's FUTURE_TOLERANCE_MS (src/utils/idleClock.js):
//    at most 60 s ahead is read as "now", anything further is expired.

/** A stamp at most this far in the future is clock jitter, read as "now" (md: FUTURE_TOLERANCE_MS). */
export const IDLE_FUTURE_TOLERANCE_MS = 60 * 1000

/** Activity is written to storage at most this often (md: STAMP_WRITE_EVERY_MS). */
export const IDLE_STAMP_WRITE_EVERY_MS = 15 * 1000

const IDLE_DIGITS = /^\d+$/
// undefined/null -> null (absent); a finite non-negative number or all-digit string -> the
// number; anything else -> NaN (unreadable).
function idleStampValue(v) {
  if (v === null || v === undefined) return null
  if (typeof v === 'number') return Number.isFinite(v) && v >= 0 ? v : NaN
  if (typeof v === 'string' && IDLE_DIGITS.test(v)) return Number(v)
  return NaN
}

/**
 * idleStartAt({ lastActivityAt, hiddenAt, now, timeoutMs }) -> { startAt } | { expired: true }
 *   `now` absent or non-finite                      -> throws (this function never reads a clock)
 *   lastActivityAt absent                           -> { startAt: now }       a fresh clock
 *   either stamp unreadable or negative             -> { expired: true }
 *   either stamp more than the tolerance ahead      -> { expired: true }
 *   lastActivityAt at most the tolerance ahead      -> { startAt: now }
 *   hiddenAt >= lastActivityAt and hidden for at
 *     least RESUME_GRACE_MS                         -> { startAt: now }       a background resets (D269)
 *   otherwise                                       -> { startAt: lastActivityAt }, or
 *                                                      { expired: true } once now - lastActivityAt >= timeoutMs
 *   a timeoutMs that is not a positive finite number -> { expired: true }
 */
export function idleStartAt({ lastActivityAt, hiddenAt, now, timeoutMs } = {}) {
  if (!Number.isFinite(now)) {
    throw new TypeError('idleStartAt: `now` must be a finite epoch — this function never reads the clock')
  }
  const last = idleStampValue(lastActivityAt)
  const hidden = idleStampValue(hiddenAt)
  if (last === null) return hidden === null || Number.isFinite(hidden) ? { startAt: now } : { expired: true }
  if (!Number.isFinite(last) || (hidden !== null && !Number.isFinite(hidden))) return { expired: true }
  if (!(typeof timeoutMs === 'number' && Number.isFinite(timeoutMs) && timeoutMs > 0)) return { expired: true }
  if (last > now + IDLE_FUTURE_TOLERANCE_MS) return { expired: true }
  if (hidden !== null && hidden > now + IDLE_FUTURE_TOLERANCE_MS) return { expired: true }
  if (last > now) return { startAt: now }
  if (hidden !== null && hidden >= last && now - hidden >= RESUME_GRACE_MS) return { startAt: now }
  if (now - last >= timeoutMs) return { expired: true }
  return { startAt: last }
}

/** Throttle for the activity write: true when nothing was written yet, the last write is
 *  IDLE_STAMP_WRITE_EVERY_MS old, or the clock moved backwards. `now` is required. */
export function shouldStampActivity(prevWrittenAt, now) {
  if (!Number.isFinite(now)) {
    throw new TypeError('shouldStampActivity: `now` must be a finite epoch')
  }
  if (prevWrittenAt === null || prevWrittenAt === undefined || !Number.isFinite(prevWrittenAt)) return true
  return now < prevWrittenAt || now - prevWrittenAt >= IDLE_STAMP_WRITE_EVERY_MS
}
